import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  SetInspectionInstanceCustomNameMutation,
  SetInspectionInstanceCustomNameMutationVariables,
} from 'graphql-query-contracts';
import { setInspectionInstanceCustomNameMutation } from 'graphql-queries';
import { SetInspectionInstanceCustomNameError } from './errors/SetInspectionInstanceCustomNameError';

export type SetInspectionInstanceCustomNameInput = {
  instanceId: string;
  channelId: string;
  customName: string | null;
  setAt: Date;
};

type InspectionEventResult =
  SetInspectionInstanceCustomNameMutation['setInspectionInstanceCustomName'];

export type UseSetInspectionInstanceCustomNameParams = {
  onError?: (error: Error) => void;
};

export type UseSetInspectionInstanceCustomNameResult = {
  setCustomName: (input: SetInspectionInstanceCustomNameInput) => Promise<InspectionEventResult>;
  loading: boolean;
  error: Error | undefined;
};

export const useSetInspectionInstanceCustomName = ({
  onError,
}: UseSetInspectionInstanceCustomNameParams = {}): UseSetInspectionInstanceCustomNameResult => {
  const [setInstanceCustomName, { loading, error }] = useMutation<
    SetInspectionInstanceCustomNameMutation,
    SetInspectionInstanceCustomNameMutationVariables
  >(setInspectionInstanceCustomNameMutation);

  const setCustomName = useCallback(
    async (input: SetInspectionInstanceCustomNameInput): Promise<InspectionEventResult> => {
      try {
        const variables: SetInspectionInstanceCustomNameMutationVariables = {
          instanceId: input.instanceId,
          channelId: input.channelId,
          customName: input.customName,
          setAt: input.setAt.toISOString(),
        };

        const result = await setInstanceCustomName({ variables });

        if (!result.data?.setInspectionInstanceCustomName) {
          throw new SetInspectionInstanceCustomNameError();
        }

        return result.data.setInspectionInstanceCustomName;
      } catch (err) {
        const wrappedError =
          err instanceof SetInspectionInstanceCustomNameError
            ? err
            : new SetInspectionInstanceCustomNameError(
                err instanceof Error ? err.message : undefined
              );

        onError?.(wrappedError);
        throw wrappedError;
      }
    },
    [setInstanceCustomName, onError]
  );

  return {
    setCustomName,
    loading,
    error,
  };
};
